import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TabsContent } from '@/components/ui/tabs';
import { formatCurrency, formatPercent } from '@/lib/utils/formatters';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { useContext } from 'react';
import { ApuracaoDetalhadaContext } from '@/contexts/ApuracaoDetalhadaContext';

export function ApuracaoIbsCbsTab() {
  const ctx = useContext(ApuracaoDetalhadaContext);
  if (!ctx) {
    throw new Error('ApuracaoIbsCbsTab deve ser usado dentro de <ApuracaoDetalhadaProvider>');
  }

  const {
    buscaAplicada,
    selecaoMensalValida,
    isLoadingTabela,
    resumoMensal,
    apuracaoLista,
    apuracaoTotais,
    isCredito,
    isEstorno,
    normalizeAliquota,
  } = ctx;

  // Alíquota efetiva = nominal × (1 − redução), em pontos percentuais, como o pAliqEfet do gRed.
  const efetiva = (aliquota: number | undefined, reducao: number) =>
    (Number(aliquota) || 0) * (1 - reducao / 100);

  // Base por item ainda não gravada nas saídas: "—" em vez de R$ 0,00.
  const formatBase = (valor: number | null) => (valor == null ? '—' : formatCurrency(valor));

  return (
    <TabsContent value="apuracao-ibs-cbs" className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="bg-primary text-primary-foreground px-6 py-3 rounded-lg"
      >
        <h2 className="text-xl font-bold">Apuração de IBS e CBS</h2>
      </motion.div>

      <AnimatePresence mode="wait" initial={false}>
        {!buscaAplicada ? (
          <motion.div
            key="apuracao-ibs-cbs-empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold">Selecione Empresa e Mês/Ano</p>
                    <p className="text-sm text-muted-foreground">
                      {selecaoMensalValida
                        ? 'Seleção pronta. Clique em Buscar no header para carregar a apuração detalhada.'
                        : 'Para consultar, selecione uma Empresa e o período no modo Mês/Ano (no modal “Selecionar Período”).'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : !selecaoMensalValida ? (
          <motion.div
            key="apuracao-ibs-cbs-invalid"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold">Selecione Empresa e Mês/Ano</p>
                    <p className="text-sm text-muted-foreground">
                      Para consultar, selecione uma Empresa e o período no modo Mês/Ano e clique em
                      Buscar.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="apuracao-ibs-cbs-content"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="space-y-6"
          >
            <div className="grid gap-6 md:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.02, ease: 'easeOut' }}
              >
                <Card>
                  <CardHeader className="pb-3">
                    <p className="text-sm text-muted-foreground">Total Entradas (Créditos)</p>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold text-lime-600">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.credito.ibs)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold text-lime-600">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.credito.cbs)
                        )}
                      </span>
                    </div>
                    {!isLoadingTabela &&
                      (resumoMensal.estorno.ibs > 0 || resumoMensal.estorno.cbs > 0) && (
                        <div className="pt-2 mt-1 border-t text-sm text-amber-700 space-y-0.5">
                          <p className="font-medium">Estorno de crédito (baixa por perda)</p>
                          <div className="flex justify-between">
                            <span>IBS:</span>
                            <span>- {formatCurrency(resumoMensal.estorno.ibs)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>CBS:</span>
                            <span>- {formatCurrency(resumoMensal.estorno.cbs)}</span>
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.06, ease: 'easeOut' }}
              >
                <Card className="border-red-200 bg-red-50">
                  <CardHeader className="pb-3">
                    <p className="text-sm text-muted-foreground">Total Saídas (Débitos)</p>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold text-red-600">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.debito.ibs)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold text-red-600">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.debito.cbs)
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.1, ease: 'easeOut' }}
              >
                <Card className="border-primary">
                  <CardHeader className="pb-3">
                    <p className="text-sm text-muted-foreground">Saldo a Pagar</p>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">IBS:</span>
                      <span className="text-lg font-semibold text-primary">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.saldo.ibs)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CBS:</span>
                      <span className="text-lg font-semibold text-primary">
                        {isLoadingTabela ? (
                          <Skeleton className="h-6 w-28" />
                        ) : (
                          formatCurrency(resumoMensal.saldo.cbs)
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.14, ease: 'easeOut' }}
            >
              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Documento</TableHead>
                        <TableHead className="text-right">Valor Operação</TableHead>
                        <TableHead className="text-right">Base Cálculo IBS</TableHead>
                        <TableHead className="text-right">Alíquota IBS</TableHead>
                        <TableHead className="text-right">Valor IBS</TableHead>
                        <TableHead className="text-right">Base Cálculo CBS</TableHead>
                        <TableHead className="text-right">Alíquota CBS</TableHead>
                        <TableHead className="text-right">Valor CBS</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingTabela
                        ? Array.from({ length: 4 }).map((_, idx) => (
                            <TableRow key={`apuracao-skeleton-${idx}`}>
                              <TableCell>
                                <Skeleton className="h-6 w-20" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-6 w-16" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-24 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-24 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-16 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-24 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-24 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-16 ml-auto" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="h-4 w-24 ml-auto" />
                              </TableCell>
                            </TableRow>
                          ))
                        : apuracaoLista.map((item, idx) => {
                            const credito = isCredito(item.Tipo);
                            const estorno = isEstorno(item.Tipo);
                            const corValor = estorno
                              ? 'text-right font-semibold text-amber-700'
                              : credito
                                ? 'text-right font-semibold'
                                : 'text-right font-semibold text-red-600';
                            // Redução de cada imposto, do seu gRed (SPEC-006 v1.5, decisão 8).
                            const reducaoCBS = Number(item.ReducaoCBS) || 0;
                            const reducoesIBS = [
                              {
                                rotulo: 'UF',
                                aliquota: item.AliquotaIBSUF,
                                reducao: Number(item.ReducaoIBSUF) || 0,
                              },
                              {
                                rotulo: 'Município',
                                aliquota: item.AliquotaIBSMun,
                                reducao: Number(item.ReducaoIBSMun) || 0,
                              },
                            ]
                              .filter(r => r.reducao > 0)
                              .map(r => ({ ...r, efetiva: efetiva(r.aliquota, r.reducao) }));
                            return (
                              <TableRow key={`apuracao-item-${idx}`}>
                                <TableCell>
                                  {estorno ? (
                                    <Badge
                                      variant="outline"
                                      className="bg-amber-50 text-amber-800 border-amber-300"
                                    >
                                      Estorno de crédito
                                    </Badge>
                                  ) : credito ? (
                                    <Badge
                                      variant="outline"
                                      className="bg-lime-50 text-lime-700 border-lime-300"
                                    >
                                      Crédito
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-red-500 text-white hover:bg-red-600">
                                      Débito
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="bg-white">
                                    {item.Documento}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(item.ValorOperacao)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatBase(item.BaseCalculoIBS)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatPercent(normalizeAliquota(item.AliquotaIBS))}
                                  {reducoesIBS.map(r => (
                                    <span
                                      key={r.rotulo}
                                      className="block text-xs text-muted-foreground"
                                    >
                                      {r.rotulo}: redução {formatPercent(r.reducao / 100)}, efetiva{' '}
                                      {formatPercent(r.efetiva / 100)}
                                    </span>
                                  ))}
                                </TableCell>
                                <TableCell className={corValor}>
                                  {formatCurrency(item.ValorIBS)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatBase(item.BaseCalculoCBS)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatPercent(normalizeAliquota(item.AliquotaCBS))}
                                  {reducaoCBS > 0 && (
                                    <span className="block text-xs text-muted-foreground">
                                      redução {formatPercent(reducaoCBS / 100)}, efetiva{' '}
                                      {formatPercent(efetiva(item.AliquotaCBS, reducaoCBS) / 100)}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className={corValor}>
                                  {formatCurrency(item.ValorCBS)}
                                </TableCell>
                              </TableRow>
                            );
                          })}

                      {!isLoadingTabela && apuracaoTotais && (
                        <TableRow className="bg-muted/50 font-bold">
                          <TableCell colSpan={2}>TOTAL GERAL</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(apuracaoTotais.TotalValorOperacao)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(apuracaoTotais.TotalBaseCalculoIBS)}
                          </TableCell>
                          <TableCell className="text-right" />
                          <TableCell className="text-right text-primary">
                            {formatCurrency(apuracaoTotais.TotalValorIBS)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(apuracaoTotais.TotalBaseCalculoCBS)}
                          </TableCell>
                          <TableCell className="text-right" />
                          <TableCell className="text-right text-primary">
                            {formatCurrency(apuracaoTotais.TotalValorCBS)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </TabsContent>
  );
}
